/**
 * StackedDrawer Component
 *
 * Visual container for layered drawer content. Handles:
 * - Z-index positioning based on layer
 * - Visual offset for stacked appearance
 * - Open/close animations
 * - Header/body/footer structure
 * - Scroll management
 * - Keyboard navigation
 *
 * @see docs/stories/DRAWER-1.3-create-stacked-drawer-component.md
 */

import React, { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useFocusTrap } from "@/hooks/useFocusTrap";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { useDrawerStack } from "@/contexts/DrawerStackContext";
import {
  ANIMATION_DURATION_MS,
  ANIMATION_EASE_IN,
  ANIMATION_EASE_OUT,
  OFFSET_PX,
  getZIndex,
  getBackdropZIndex,
  getOffset,
  WIDTH_CLASSES,
  CONTAINED_LEFT_MARGIN,
  type DrawerWidth,
} from "./drawer-constants";

// Re-export types and constants for convenience
export type { DrawerWidth };

export interface StackedDrawerProps {
  /** Unique identifier for the drawer */
  id: string;
  /** Layer number (1, 2, or 3) */
  layer: number;
  /** Whether the drawer is currently visible */
  isOpen: boolean;
  /** Callback when drawer should close */
  onClose: () => void;
  /** Title displayed in header */
  title: string;
  /** Optional subtitle below title */
  subtitle?: string;
  /** Main content of the drawer */
  children: React.ReactNode;
  /** Optional footer content (typically action buttons) */
  footer?: React.ReactNode;
  /** Width of the drawer */
  width?: DrawerWidth;
  /** Whether clicking backdrop closes drawer */
  closeOnBackdropClick?: boolean;
  /** Whether pressing Escape closes drawer */
  closeOnEscape?: boolean;
  /** Whether to preserve scroll position when re-opening */
  preserveScroll?: boolean;
  /** Callback when scroll position changes */
  onScrollChange?: (scrollTop: number) => void;
  /** Callback when open/close animation completes */
  onAnimationComplete?: (isOpen: boolean) => void;
  /** Custom class name for the drawer panel */
  className?: string;
  /** Custom class name for the body section */
  bodyClassName?: string;
  /** Whether to show the header section */
  showHeader?: boolean;
  /** Custom header content (replaces default header) */
  headerContent?: React.ReactNode;
  /** Whether the drawer is currently pulsing (for duplicate detection feedback) */
  isPulsing?: boolean;
  /** Whether the close button should be highlighted (max drawer feedback) */
  highlightCloseButton?: boolean;
  /**
   * Contained mode: renders drawer inside parent container instead of portal to body.
   * When true, drawer positions itself absolutely within its parent.
   */
  contained?: boolean;
}


// ============================================================================
// Component
// ============================================================================

export function StackedDrawer({
  id,
  layer,
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footer,
  width = "lg",
  closeOnBackdropClick = true,
  closeOnEscape = true,
  preserveScroll = true,
  onScrollChange,
  onAnimationComplete,
  className,
  bodyClassName,
  showHeader = true,
  headerContent,
  isPulsing = false,
  highlightCloseButton = false,
  contained = false,
}: StackedDrawerProps) {
  // =========================================================================
  // Context
  // =========================================================================

  // Get highlightCloseButtons and viewMode from context
  // Falls back to prop if not in context (for testing)
  const context = useDrawerStack();
  const shouldHighlightClose = highlightCloseButton || context.highlightCloseButtons;
  const viewMode = context.viewMode;
  const canSplitView = context.canSplitView;
  const { bringToFront, stackDepth } = context;

  // Determine if this drawer is in the background (not the topmost)
  const isBackgroundDrawer = layer < stackDepth;
  // Only apply split view if mode is "split" AND we still have 2 active drawers
  // This ensures smooth transition back to stack when one drawer closes
  const isSplitView = viewMode === "split" && canSplitView && contained;

  // Check for reduced motion preference
  const prefersReducedMotion = useReducedMotion();

  // =========================================================================
  // State & Refs
  // =========================================================================

  // State for animation - start with false to allow opening animation
  const [isAnimatingIn, setIsAnimatingIn] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [showCloseButton, setShowCloseButton] = useState(false);
  const [contentVisible, setContentVisible] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const previousScrollRef = useRef(0);
  const animationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeButtonTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // =========================================================================
  // Focus Trap
  // =========================================================================

  // Trap focus within drawer when open
  useFocusTrap(drawerRef, {
    isActive: isOpen && isAnimatingIn && !isAnimatingOut,
    autoFocus: true,
    returnFocus: true,
  });

  // =========================================================================
  // Calculated Values
  // =========================================================================

  const zIndex = getZIndex(layer);
  const backdropZIndex = getBackdropZIndex(layer);
  const offset = getOffset(layer);

  // =========================================================================
  // Animation State Machine
  // =========================================================================

  // Handle opening animation
  useEffect(() => {
    if (!isOpen) return;

    // Start opening
    setShouldRender(true);

    let contentDelayTimer: ReturnType<typeof setTimeout> | null = null;

    // Staggered animation for smooth entrance
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setIsAnimatingIn(true);
        setIsAnimatingOut(false);

        // Show content with slight delay for layered effect
        const contentDelay = prefersReducedMotion ? 0 : 100;
        contentDelayTimer = setTimeout(() => setContentVisible(true), contentDelay);

        // Show close button after drawer slides in
        const closeDelay = prefersReducedMotion ? 50 : ANIMATION_DURATION_MS;
        closeButtonTimeoutRef.current = setTimeout(() => {
          setShowCloseButton(true);
        }, closeDelay);
      });
    });

    // Animation complete callback
    animationTimeoutRef.current = setTimeout(() => {
      onAnimationComplete?.(true);
    }, ANIMATION_DURATION_MS);

    return () => {
      if (contentDelayTimer) clearTimeout(contentDelayTimer);
      if (closeButtonTimeoutRef.current) {
        clearTimeout(closeButtonTimeoutRef.current);
        closeButtonTimeoutRef.current = null;
      }
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
        animationTimeoutRef.current = null;
      }
    };
  }, [isOpen, onAnimationComplete, prefersReducedMotion]);

  // Handle closing animation - triggered when isOpen becomes false while shouldRender is true
  // OR when drawer mounts already in closing state (cleanup zombie drawers)
  useEffect(() => {
    // If drawer is closing but never rendered (zombie drawer), just call onAnimationComplete immediately
    if (!isOpen && !shouldRender) {
      onAnimationComplete?.(false);
      return;
    }

    if (isOpen || !shouldRender) return;

    // Start closing - hide elements first for smooth exit
    setShowCloseButton(false);
    setContentVisible(false);

    // Small delay before starting drawer slide-out
    const exitDelay = prefersReducedMotion ? 0 : 50;
    const exitTimer = setTimeout(() => {
      setIsAnimatingIn(false);
      setIsAnimatingOut(true);
    }, exitDelay);

    // Wait for animation then unmount
    const unmountTimer = setTimeout(() => {
      setShouldRender(false);
      setIsAnimatingOut(false);
      onAnimationComplete?.(false);
    }, ANIMATION_DURATION_MS + exitDelay);

    return () => {
      clearTimeout(exitTimer);
      clearTimeout(unmountTimer);
    };
  }, [isOpen, shouldRender, onAnimationComplete, prefersReducedMotion, id]);

  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
      if (closeButtonTimeoutRef.current) {
        clearTimeout(closeButtonTimeoutRef.current);
      }
    };
  }, []);

  // =========================================================================
  // Escape Key Handler
  // =========================================================================

  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // Check if a dialog/alertdialog is open - they take precedence
        // This includes UnsavedChangesDialog, confirmation dialogs, etc.
        const openDialog = document.querySelector(
          '[role="alertdialog"], [role="dialog"][data-state="open"]'
        );
        if (openDialog) {
          // Let the dialog handle Escape
          return;
        }

        // Prevent bubbling to parent drawers
        e.stopPropagation();
        e.preventDefault();
        onClose();
      }
    };

    // Use capture phase to intercept before other handlers
    document.addEventListener("keydown", handleKeyDown, { capture: true });
    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [isOpen, closeOnEscape, onClose]);

  // =========================================================================
  // Scroll Preservation
  // =========================================================================

  useEffect(() => {
    if (isOpen && preserveScroll && bodyRef.current) {
      // Restore previous scroll position
      bodyRef.current.scrollTop = previousScrollRef.current;
    }
  }, [isOpen, preserveScroll]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const scrollTop = e.currentTarget.scrollTop;
      previousScrollRef.current = scrollTop;
      onScrollChange?.(scrollTop);
    },
    [onScrollChange]
  );

  // =========================================================================
  // Backdrop Click Handler
  // =========================================================================

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      // Only handle clicks directly on backdrop, not bubbled clicks
      if (e.target === e.currentTarget && closeOnBackdropClick) {
        onClose();
      }
    },
    [closeOnBackdropClick, onClose]
  );

  // =========================================================================
  // Bring to Front Click Handler
  // =========================================================================

  const handleBringToFront = useCallback(
    (e: React.MouseEvent) => {
      // Prevent event from bubbling to backdrop
      e.stopPropagation();
      if (isBackgroundDrawer && bringToFront) {
        bringToFront(id);
      }
    },
    [isBackgroundDrawer, bringToFront, id]
  );

  // =========================================================================
  // Body Scroll Lock
  // =========================================================================

  useEffect(() => {
    if (!isOpen) return;

    // Only lock body scroll for first layer to prevent conflicts
    if (layer === 1) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";

      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen, layer]);

  // =========================================================================
  // Render
  // =========================================================================

  // Don't render if not needed
  if (!shouldRender) {
    return null;
  }

  // Determine animation state
  const isVisible = isAnimatingIn && !isAnimatingOut;

  // Calculate left position for contained mode
  // In split view: Layer 1 = left half, Layer 2 = right half (50% each)
  // In stack view: Each layer adds OFFSET_PX to create visual stacking effect
  //   Layer 1: base margin (48px), Layer 2: 48+24=72px, Layer 3: 48+48=96px
  const getSplitViewPosition = () => {
    if (!isSplitView) return null;
    // Split view: each drawer takes 50% width
    // Layer 1 on left (starts at CONTAINED_LEFT_MARGIN)
    // Layer 2 on right (starts at 50%)
    if (layer === 1) {
      return {
        left: `${CONTAINED_LEFT_MARGIN}px`,
        right: "50%",
        width: undefined,
      };
    } else {
      return {
        left: "50%",
        right: 0,
        width: undefined,
      };
    }
  };

  const splitPosition = getSplitViewPosition();

  const containedLeft = contained && !isSplitView
    ? CONTAINED_LEFT_MARGIN + (layer - 1) * OFFSET_PX
    : undefined;

  // Calculate close button position (matches drawer left edge)
  const closeButtonLeft = (() => {
    if (!contained) return undefined;
    if (isSplitView) {
      // In split view, position close button at left edge of each panel
      if (layer === 1) {
        return CONTAINED_LEFT_MARGIN - 16;
      } else {
        // For layer 2, we need to calculate where "50%" is in pixels
        // We'll use calc() in the style instead
        return undefined;
      }
    }
    return (containedLeft ?? 0) - 16;
  })();

  // In split view, only layer 1 shows the backdrop (shared backdrop for both)
  // In stack view, each layer shows its own backdrop
  // Always show backdrop for the topmost visible drawer to ensure there's always one
  const showBackdrop = !isSplitView ? true : layer === 1;

  // Drawer content (shared between portal and contained modes)
  const drawerContent = (
    <>
      {/* Backdrop - in split view, only layer 1 renders the shared backdrop */}
      {showBackdrop && (
        <div
          className={cn(
            contained ? "absolute inset-0" : "fixed inset-0",
            "bg-black/20",
            isVisible ? "opacity-100" : "opacity-0"
          )}
          style={{
            zIndex: contained ? 1 : backdropZIndex, // Low z-index so it's behind all drawers
            transition: prefersReducedMotion
              ? "none"
              : `opacity ${ANIMATION_DURATION_MS}ms ${isVisible ? ANIMATION_EASE_IN : ANIMATION_EASE_OUT}`,
          }}
          onClick={handleBackdropClick}
          aria-hidden="true"
        />
      )}

      {/* Floating Edge Close Button - positioned outside drawer to avoid clipping */}
      <button
        onClick={onClose}
        className={cn(
          contained ? "absolute" : "fixed",
          "top-6 h-8 w-8 rounded-full bg-primary flex items-center justify-center",
          "hover:bg-primary/90 hover:scale-110",
          "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
          "shadow-lg",
          shouldHighlightClose && "animate-glow-attention"
        )}
        style={{
          zIndex: contained ? layer * 10 + 6 : zIndex + 1,
          // Position close button at left edge of drawer
          left: isSplitView && layer === 2
            ? "calc(50% - 16px)" // For layer 2 in split view, position at 50% - half button width
            : contained
              ? `${closeButtonLeft}px`
              : undefined,
          opacity: showCloseButton ? 1 : 0,
          transform: showCloseButton
            ? `translateX(0) scale(1)`
            : `translateX(20px) scale(0.75)`,
          transition: prefersReducedMotion
            ? "none"
            : showCloseButton
              ? `opacity 400ms ${ANIMATION_EASE_IN}, transform 400ms ${ANIMATION_EASE_IN}, left ${ANIMATION_DURATION_MS}ms ease-out`
              : `opacity 200ms ${ANIMATION_EASE_OUT}, transform 200ms ${ANIMATION_EASE_OUT}`,
          boxShadow: showCloseButton
            ? "0 8px 25px rgba(0, 0, 0, 0.15), 0 4px 10px rgba(0, 0, 0, 0.1)"
            : "0 2px 8px rgba(0, 0, 0, 0.1)",
          pointerEvents: showCloseButton ? "auto" : "none",
        }}
        aria-label="Close drawer"
      >
        <X
          className="h-4 w-4 text-primary-foreground"
          style={{
            transform: showCloseButton ? "rotate(0deg)" : "rotate(45deg)",
            transition: prefersReducedMotion ? "none" : "transform 200ms ease-out",
          }}
        />
      </button>

      {/* Drawer Panel */}
      <div
        ref={drawerRef}
        id={id}
        role="dialog"
        aria-modal="true"
        aria-labelledby={showHeader ? `${id}-title` : undefined}
        aria-describedby={subtitle ? `${id}-subtitle` : undefined}
        tabIndex={-1}
        className={cn(
          // Contained mode: position within parent; Portal mode: fixed to viewport
          contained ? "absolute inset-y-0" : "fixed inset-y-0 right-0",
          "bg-background border-l",
          // Only round left corners if not in split view or if layer 1 (leftmost)
          isSplitView ? (layer === 1 ? "rounded-l-xl" : "border-l") : "rounded-l-xl",
          // In split view layer 2, also add right border for visual separation
          isSplitView && layer === 2 && "border-r",
          "flex flex-col overflow-hidden",
          // In contained mode, width stretches from left to right-0; otherwise use predefined classes
          !contained && WIDTH_CLASSES[width],
          isPulsing && !prefersReducedMotion && "animate-drawer-pulse",
          className
        )}
        style={{
          zIndex: contained ? layer * 10 + 5 : zIndex,
          // Split view mode: use calculated positions
          // Stack mode (contained): set left offset and right:0 to fill available space
          // Portal mode: use marginLeft for layer offset
          ...(isSplitView && splitPosition
            ? {
                left: splitPosition.left,
                right: splitPosition.right,
              }
            : {
                left: contained ? `${containedLeft}px` : undefined,
                right: contained ? 0 : undefined,
              }),
          marginLeft: !contained && offset > 0 ? `${offset}px` : undefined,
          transform: isVisible ? "translateX(0)" : "translateX(100%)",
          boxShadow: isVisible
            ? isSplitView
              ? "0 4px 20px rgba(0, 0, 0, 0.1)" // Lighter shadow in split view
              : "0 25px 50px -12px rgba(0, 0, 0, 0.25), -4px 0 20px rgba(0, 0, 0, 0.1)"
            : "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
          transition: prefersReducedMotion
            ? "none"
            : `transform ${ANIMATION_DURATION_MS}ms ${isVisible ? ANIMATION_EASE_IN : ANIMATION_EASE_OUT}, box-shadow ${ANIMATION_DURATION_MS}ms ease-out, left ${ANIMATION_DURATION_MS}ms ease-out, right ${ANIMATION_DURATION_MS}ms ease-out`,
          willChange: "transform",
        }}
      >
        {/* Click-to-front overlay for background drawers */}
        {isBackgroundDrawer && !isSplitView && (
          <div
            className="absolute inset-0 z-10 cursor-pointer group"
            onClick={handleBringToFront}
            role="button"
            tabIndex={0}
            aria-label={`Bring ${title} drawer to front`}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleBringToFront(e as unknown as React.MouseEvent);
              }
            }}
          >
            {/* Subtle hover indicator */}
            <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/5 transition-colors duration-200" />
            {/* Visual hint on hover */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
              <div className="bg-background/95 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-lg border text-sm font-medium text-muted-foreground">
                Click to bring to front
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        {showHeader && (
          <div
            className={cn(
              "flex items-center justify-between pl-8 pr-6 py-4 border-b bg-background flex-shrink-0",
              contentVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-1"
            )}
            style={{
              transition: prefersReducedMotion
                ? "none"
                : contentVisible
                  ? `opacity 300ms ${ANIMATION_EASE_IN} 50ms, transform 300ms ${ANIMATION_EASE_IN} 50ms`
                  : `opacity 150ms ${ANIMATION_EASE_OUT}, transform 150ms ${ANIMATION_EASE_OUT}`,
            }}
          >
            {headerContent || (
              <div className="min-w-0 flex-1">
                <h2
                  id={`${id}-title`}
                  className="text-lg font-semibold text-foreground truncate"
                >
                  {title}
                </h2>
                {subtitle && (
                  <p
                    id={`${id}-subtitle`}
                    className="text-sm text-muted-foreground truncate"
                  >
                    {subtitle}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Scrollable Body */}
        <div
          ref={bodyRef}
          className={cn(
            "flex-1 overflow-y-auto",
            contentVisible ? "opacity-100" : "opacity-0",
            bodyClassName
          )}
          style={{
            transition: prefersReducedMotion
              ? "none"
              : contentVisible
                ? `opacity 400ms ${ANIMATION_EASE_IN} 100ms`
                : `opacity 200ms ${ANIMATION_EASE_OUT}`,
          }}
          onScroll={handleScroll}
        >
          {/* Only render children after animation has started to prevent layout shifts */}
          {isAnimatingIn && children}
        </div>

        {/* Footer */}
        {footer && (
          <div
            className={cn(
              "px-6 py-4 border-t bg-background flex-shrink-0",
              contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1"
            )}
            style={{
              transition: prefersReducedMotion
                ? "none"
                : contentVisible
                  ? `opacity 300ms ${ANIMATION_EASE_IN} 150ms, transform 300ms ${ANIMATION_EASE_IN} 150ms`
                  : `opacity 150ms ${ANIMATION_EASE_OUT}, transform 150ms ${ANIMATION_EASE_OUT}`,
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </>
  );

  // Contained mode: render inline; Portal mode: render to body
  if (contained) {
    return drawerContent;
  }

  return createPortal(drawerContent, document.body);
}

// Re-export constants for convenience
export {
  Z_INDEX_BASE,
  Z_INDEX_INCREMENT,
  OFFSET_PX,
  CONTAINED_LEFT_MARGIN,
  ANIMATION_DURATION_MS,
  ANIMATION_EASE_IN,
  ANIMATION_EASE_OUT,
  getZIndex,
  getBackdropZIndex,
  getOffset,
} from "./drawer-constants";
